import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ClickAwayListener, IconButton, InputAdornment, Paper, Popper, TextField } from '@mui/material';
import { CalendarMonth, ChevronLeft, ChevronRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { DATE_PICKER_MONTHS } from '../appConstants';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
export const POPPER_DATE_PICKER_HEIGHT = 320;
const VIEW_CALENDAR = 'calendar';
const VIEW_MONTH = 'month';
const VIEW_YEAR = 'year';

const padDatePart = (value) => String(value).padStart(2, '0');

const formatDateValue = (date) => (
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`
);

const parseDateValue = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const PopperDateField = ({
  value,
  onChange,
  onFocus,
  ariaLabel,
  icon,
  className = '',
  iconClassName = 'filter-header-date-picker-icon',
  buttonClassName = 'filter-header-date-picker-button',
  iconUrlVar = '--filter-header-date-icon-url',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selectedDate = useMemo(() => parseDateValue(value), [value]);
  const [visibleMonth, setVisibleMonth] = useState(() => selectedDate || new Date());
  const [viewMode, setViewMode] = useState(VIEW_CALENDAR);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const instanceId = useMemo(() => Math.random().toString(36).substring(2, 9), []);

  useEffect(() => {
    if (open) {
      const nextMonth = selectedDate || new Date();
      setVisibleMonth(nextMonth);
      setViewMode(VIEW_CALENDAR);
    }
  }, [open, selectedDate]);

  const startOfMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const calendarStart = new Date(startOfMonth);
  calendarStart.setDate(calendarStart.getDate() - calendarStart.getDay());

  const days = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(calendarStart);
    date.setDate(calendarStart.getDate() + index);
    return date;
  });

  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Select date';

  const renderedIcon = typeof icon === 'string'
    ? (
      <span
        className={iconClassName}
        style={{ [iconUrlVar]: `url("${icon}")` }}
        aria-hidden="true"
      />
    )
    : React.cloneElement(icon || <CalendarMonth />, {
      className: [icon?.props?.className, iconClassName].filter(Boolean).join(' '),
    });

  const openPicker = (event) => {
    if (disabled) return;
    onFocus?.(event);
    setOpen(true);
    document.dispatchEvent(new CustomEvent('fp-popup-open-intent', { detail: instanceId }));
  };

  const togglePicker = (event) => {
    if (disabled) return;
    onFocus?.(event);
    setOpen((current) => {
      if (!current) {
        document.dispatchEvent(new CustomEvent('fp-popup-open-intent', { detail: instanceId }));
      }
      return !current;
    });
  };

  useEffect(() => {
    const handleOtherOpen = (e) => {
      if (e.detail !== instanceId) {
        setOpen(false);
      }
    };
    document.addEventListener('fp-popup-open-intent', handleOtherOpen);
    return () => document.removeEventListener('fp-popup-open-intent', handleOtherOpen);
  }, [instanceId]);

  const closePicker = (event) => {
    if (anchorRef.current && event?.target && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const moveMonth = (amount) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
  };

  const moveYear = (amount) => {
    setVisibleMonth((current) => new Date(current.getFullYear() + amount, current.getMonth(), 1));
  };

  const selectMonth = (monthIndex) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), monthIndex, 1));
    setViewMode(VIEW_CALENDAR);
  };

  const selectYear = (year) => {
    setVisibleMonth((current) => new Date(year, current.getMonth(), 1));
    setViewMode(VIEW_MONTH);
  };

  const selectDate = (date) => {
    onChange?.({ target: { value: formatDateValue(date) } });
    setOpen(false);
  };

  const clearDate = () => {
    onChange?.({ target: { value: '' } });
    setOpen(false);
  };

  const selectToday = () => {
    selectDate(new Date());
  };

  return (
    <ClickAwayListener onClickAway={closePicker}>
      <div className="fp-date-picker-anchor">
      <TextField
        ref={anchorRef}
        size="small"
        value={displayValue}
        className={[
          'fp-date-field',
          value ? 'filter-header-date-field--selected' : '',
          className
        ].filter(Boolean).join(' ')}
        data-fp-date-field="true"
        data-fp-date-open={open ? 'true' : undefined}
        disabled={disabled}
        onClick={togglePicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
          }
          if (event.key === 'Escape') {
            setOpen(false);
          }
          event.preventDefault();
        }}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          'aria-expanded': open,
          'aria-label': ariaLabel,
          'data-lpignore': 'true',
          'data-form-type': 'other',
          autoComplete: 'off',
          readOnly: true,
          role: 'combobox',
        }}
        inputRef={inputRef}
        autoComplete="off"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                className={buttonClassName}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  // Let the event bubble up to the TextField's onClick handler
                  // so that ClickAwayListeners on other dropdowns can fire
                }}
                aria-label={ariaLabel}
              >
                {renderedIcon}
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          width: '100%',
          '.MuiInputBase-root': { height: '32px' },
        }}
        onFocus={openPicker}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1500 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
          {
            name: 'flip',
            enabled: true,
            options: {
              boundary: 'viewport',
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              boundary: 'viewport',
            },
          },
        ]}
      >
        <Paper elevation={8} className="fp-date-picker-paper">
          <div className="fp-date-picker__header">
            {viewMode === VIEW_CALENDAR && (
              <>
                <IconButton size="small" onClick={() => moveYear(-1)} aria-label="Previous year">
                  <KeyboardDoubleArrowLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveMonth(-1)} aria-label="Previous month">
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <div className="fp-date-picker__title-group">
                  <button
                    type="button"
                    className="fp-date-picker__title-btn"
                    onClick={() => setViewMode(VIEW_MONTH)}
                  >
                    {DATE_PICKER_MONTHS[visibleMonth.getMonth()]}
                  </button>
                  <button
                    type="button"
                    className="fp-date-picker__title-btn"
                    onClick={() => setViewMode(VIEW_YEAR)}
                  >
                    {visibleMonth.getFullYear()}
                  </button>
                </div>
                <IconButton size="small" onClick={() => moveMonth(1)} aria-label="Next month">
                  <ChevronRight fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveYear(1)} aria-label="Next year">
                  <KeyboardDoubleArrowRight fontSize="small" />
                </IconButton>
              </>
            )}
            {viewMode === VIEW_MONTH && (
              <>
                <IconButton size="small" onClick={() => moveYear(-1)} aria-label="Previous year">
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <button
                  type="button"
                  className="fp-date-picker__title"
                  onClick={() => {
                    setViewMode(VIEW_YEAR);
                  }}
                >
                  {visibleMonth.getFullYear()}
                </button>
                <IconButton size="small" onClick={() => moveYear(1)} aria-label="Next year">
                  <ChevronRight fontSize="small" />
                </IconButton>
              </>
            )}
            {viewMode === VIEW_YEAR && (
              <>
                <div style={{ flex: 1 }} />
                <button type="button" className="fp-date-picker__title" onClick={() => setViewMode(VIEW_CALENDAR)}>
                  Select Year
                </button>
                <div style={{ flex: 1 }} />
              </>
            )}
          </div>
          {viewMode === VIEW_CALENDAR && (
            <>
              <div className="fp-date-picker__weekdays">
                {WEEKDAYS.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
              <div className="fp-date-picker__grid">
                {days.map((date) => {
                  const dateValue = formatDateValue(date);
                  const isSelected = value === dateValue;
                  const isOutsideMonth = date.getMonth() !== visibleMonth.getMonth();
                  const isToday = dateValue === formatDateValue(new Date());

                  return (
                    <button
                      key={dateValue}
                      type="button"
                      className={[
                        'fp-date-picker__day',
                        isSelected ? 'fp-date-picker__day--selected' : '',
                        isOutsideMonth ? 'fp-date-picker__day--muted' : '',
                        isToday ? 'fp-date-picker__day--today' : ''
                      ].filter(Boolean).join(' ')}
                      onClick={() => selectDate(date)}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {viewMode === VIEW_MONTH && (
            <div className="fp-date-picker__month-grid">
              {DATE_PICKER_MONTHS.map((month, index) => (
                <button
                  key={month}
                  type="button"
                  className={`fp-date-picker__month-card${visibleMonth.getMonth() === index ? ' is-selected' : ''}`}
                  onClick={() => selectMonth(index)}
                >
                  {month}
                </button>
              ))}
            </div>
          )}
          {viewMode === VIEW_YEAR && (
            <div
              className="fp-date-picker__year-list"
              ref={(node) => {
                if (node && viewMode === VIEW_YEAR && open) {
                  const targetYear = visibleMonth.getFullYear();
                  const yearElement = node.querySelector(`[data-year="${targetYear}"]`);
                  if (yearElement) {
                    yearElement.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
                  }
                }
              }}
            >
              {Array.from({ length: 100 }, (_, index) => new Date().getFullYear() - 50 + index).map((year) => (
                <button
                  key={year}
                  data-year={year}
                  type="button"
                  className={`fp-date-picker__year-chip${visibleMonth.getFullYear() === year ? ' is-selected' : ''}`}
                  onClick={() => selectYear(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
          <div className="fp-date-picker__actions">
            <button type="button" className="fp-date-picker__action" onClick={clearDate}>
              Clear
            </button>
            <button type="button" className="fp-date-picker__action" onClick={selectToday}>
              Today
            </button>
          </div>
        </Paper>
      </Popper>
      </div>
    </ClickAwayListener>
  );
};

PopperDateField.displayName = 'PopperDateField';

PopperDateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  ariaLabel: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  className: PropTypes.string,
  iconClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  iconUrlVar: PropTypes.string,
  disabled: PropTypes.bool,
};

export default PopperDateField;
